import React, { useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import SVG from 'react-inlinesvg';
import { getSystemBanners } from '../../../api/systemBanner';
import Pagination from '../../../components/Pagination';
import SimpleTable from '../../../components/SimpleTable';
import useModal from '../../../context/ModalContext';
import EditIcon from '../../../../../../assets/images/reskin-images/icon--edit.svg';
import BinIcon from '../../../../../../assets/images/reskin-images/icon--bin.svg';
import DeleteModal from './components/DeleteModal';

const SystemBanners = ({
  searchQuery,
  filter,
  setOpenSideActionBoard,
  setRefetchBanners,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const { setContent } = useModal();

  const { data, refetch } = useQuery(
    ['getSystemBanners', { page: currentPage, search: searchQuery, filter }],
    getSystemBanners
  );

  useEffect(() => {
    if (setRefetchBanners) {
      setRefetchBanners(() => refetch);
    }
  }, [refetch, setRefetchBanners]);

  useEffect(() => {
    refetch();
  }, [currentPage, searchQuery, filter, refetch]);

  const columns = [
    {
      label: 'Title',
      field: 'title',
      textAlign: 'left',
      fontWeight: 'semibold',
      width: '200px',
      onHover: (item) => (
        <div className="absolute pin-r pin-t h-full items-center pr-2 flex">
          <button
            type="button"
            className="flex items-center justify-content-center bg-purple-100 p-2 rounded-lg hover:bg-purple-200"
            onClick={() =>
              setOpenSideActionBoard({ type: 'edit', id: item.id })
            }
          >
            <SVG src={EditIcon} />
          </button>
          <button
            type="button"
            className="flex items-center justify-content-center bg-purple-100 hover:bg-red-lighter rounded-lg p-2"
            onClick={() => {
              setContent({
                content: <DeleteModal id={item.id} refetch={refetch} />,
              });
            }}
          >
            <SVG src={BinIcon} />
          </button>
        </div>
      ),
    },
    {
      label: 'Type',
      field: 'banner_type',
      textAlign: 'center',
      fontWeight: 'semibold',
      width: '100px',
    },
    {
      label: 'Application',
      field: 'application',
      textAlign: 'center',
      fontWeight: 'semibold',
      width: '120px',
    },
    {
      label: 'Start',
      field: 'active_start',
      textAlign: 'left',
      fontWeight: 'semibold',
      width: '150px',
    },
    {
      label: 'End',
      field: 'active_end',
      textAlign: 'left',
      fontWeight: 'semibold',
      width: '150px',
    },
    {
      label: 'Status',
      field: 'status',
      textAlign: 'center',
      fontWeight: 'semibold',
      width: '100px',
    },
  ];

  const getBannerTypeButton = (type) => {
    const typeStyles = {
      info: 'bg-blue-100 text-blue-800',
      warning: 'bg-yellow-100 text-yellow-800',
      error: 'bg-red-100 text-red-800',
      maintenance: 'bg-orange-100 text-orange-800',
    };

    return (
      <div
        className={`flex items-center justify-center rounded-lg px-2 py-1 text-sm ${
          typeStyles[type] || 'bg-gray-100 text-gray-800'
        }`}
      >
        {type}
      </div>
    );
  };

  const getStatusButton = (status) => {
    const statusStyles = {
      active: 'bg-green-100 text-green-800',
      upcoming: 'bg-blue-100 text-blue-800',
      expired: 'bg-gray-100 text-gray-800',
    };

    return (
      <div
        className={`flex items-center justify-center rounded-lg px-2 py-1 text-sm ${
          statusStyles[status] || 'bg-gray-100 text-gray-800'
        }`}
      >
        {status}
      </div>
    );
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const renderRowFn = (banner) => ({
    id: banner.id,
    title: banner.title,
    banner_type: getBannerTypeButton(banner.banner_type),
    application: banner.application === 'all' ? 'All Apps' : banner.application,
    active_start: formatDateTime(banner.active_start),
    active_end: formatDateTime(banner.active_end),
    status: getStatusButton(banner.status),
  });

  return (
    <>
      {data?.system_banners && (
        <div>
          <SimpleTable
            data={data.system_banners}
            columns={columns}
            rowFormatter={renderRowFn}
          />
        </div>
      )}

      <div style={{ paddingTop: 20 }}>
        <Pagination
          currentPage={currentPage}
          totalPage={data?.pagination?.total_pages}
          onPageChange={(page) => setCurrentPage(page)}
        />
      </div>
    </>
  );
};

export default SystemBanners;

